/**
 * This composable pollutes the global namespace with Vue 2 and Quasar 1.
 * Required for supporting current Smartblock.
 */
import { ref, Vue2, install } from 'vue-demi'
import * as VueCompositionApiModule from 'vue-demi'

if (import.meta.env.VITE_QUASAR_V1_EXTERNALIZED_CSS_URLS == null) {
  console.warn('VITE_QUASAR_V1_EXTERNALIZED_CSS_URLS is not set. Quasar V1 will not be loaded properly.')
}

const shimCssUrl: string[] = (import.meta.env.VITE_QUASAR_V1_EXTERNALIZED_CSS_URLS ?? '').split(',')

async function importCssAsync (url: string): Promise<void> {
  return await new Promise((resolve) => {
    if (document.querySelector(`link[href="${url}"]`) != null) {
      resolve()
      return
    }
    const el = document.createElement('link')
    el.rel = 'stylesheet'
    el.type = 'text/css'
    el.href = url
    el.onload = () => {
      resolve()
    }
    document.getElementsByTagName('head')[0].appendChild(el)
  })
}

let quasarPromise: Promise<void>

async function importQuasarUmd (quasarConfig?: any): Promise<void> {
  if (quasarPromise == null) {
    quasarPromise = import('./quasar').then(({ default: Quasar, install: quasarInstall }) => {
      // @ts-expect-error
      if (!('QuasarV1' in window)) window.QuasarV1 = Quasar
      quasarInstall(Vue2, quasarConfig ?? {
        loadingBar: {
          skipHijack: true
        }
      })
    })
  }
  return await quasarPromise
}

export interface useQuasarV1ShimOptions {
  quasarConfig?: any
}

export function useQuasarV1Shim (enabled: boolean, opt: useQuasarV1ShimOptions = {}) {
  if (!enabled) {
    return {
      readyPromise: Promise.resolve(),
      isReady: true,
      quasarWrapperClass: ''
    }
  }
  if (!('Vue' in window)) {
    // @ts-expect-error
    window.Vue = Vue2
  }
  // FIXME hardcoded Vue 2.6 for smartblock support.
  if (!('VueCompositionApi' in window)) {
    install()
    // @ts-expect-error
    window.VueCompositionApi = VueCompositionApiModule
  }
  const isReady = ref<boolean>(false)
  // Add the shim CSS to the document if it's not already there
  const readyPromise = Promise.all([
    ...shimCssUrl.map(async url => await importCssAsync(url)),
    importQuasarUmd(opt?.quasarConfig)
  ]).then(() => {
    isReady.value = true
  }).catch(console.error)
  return {
    readyPromise,
    isReady,
    quasarWrapperClass: 'quasar-v1-externalized'
  }
}
